import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { queryKeys } from '@packages/api';
import type { AccountSearchQuery } from '@packages/types';
import { searchAccount } from '@packages/api/endpoints/account-search.js';
import { useCopy } from '../../hooks/useCopy';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import {
  clientRowFromSearch,
  invoiceRowFromSearch,
  matterRowFromSearch,
  type OmniboxGroup,
} from './omnibox.items';

const SEARCH_DEBOUNCE_MS = 300;
/** The search schema rejects a longer query. */
const MAX_QUERY_LENGTH = 200;
/** Per group, so all three groups fit in the panel without scrolling. */
const RESULTS_PER_GROUP = 3;

/**
 * Debounced account search for the "Search or ask Bill" field. Runs only while
 * the popover is open and the field has text; `staleTime: 0` because invoice
 * rows change from too many places (including async listeners) for any list of
 * invalidations to cover. `settled` is true once the rows are the answer to the
 * text as typed.
 */
export function useOmniboxSearch({
  accountId,
  text,
  open,
  invoicesVisible,
}: {
  accountId: string;
  text: string;
  open: boolean;
  invoicesVisible: boolean;
}): { groups: OmniboxGroup[]; isLoading: boolean; isError: boolean; settled: boolean } {
  const copy = useCopy();
  const trimmed = text.trim().slice(0, MAX_QUERY_LENGTH);
  const debounced = useDebouncedValue(trimmed, SEARCH_DEBOUNCE_MS);
  const effectiveQuery = trimmed === '' ? '' : debounced;
  const include: AccountSearchQuery['include'] = invoicesVisible
    ? ['clients', 'matters', 'invoices']
    : ['clients', 'matters'];

  const query = useQuery({
    queryKey: queryKeys.accountSearch(accountId, {
      q: effectiveQuery,
      include,
      limit: RESULTS_PER_GROUP,
    }),
    queryFn: () => searchAccount(accountId, { q: effectiveQuery, include, limit: RESULTS_PER_GROUP }),
    enabled: open && effectiveQuery !== '',
    placeholderData: keepPreviousData,
    staleTime: 0,
  });

  const data = query.data;
  const groups: OmniboxGroup[] = [];
  if (data?.clients?.length) {
    groups.push({ heading: copy('omnibox:groups.clients'), rows: data.clients.map(clientRowFromSearch) });
  }
  if (data?.matters?.length) {
    groups.push({
      heading: copy('omnibox:groups.matters'),
      rows: data.matters.map((hit) => matterRowFromSearch(hit, copy)),
    });
  }
  if (invoicesVisible && data?.invoices?.length) {
    groups.push({
      heading: copy('omnibox:groups.invoices'),
      rows: data.invoices.map((hit) => invoiceRowFromSearch(hit, copy)),
    });
  }

  // Text with no data yet is still loading while the debounce runs, so a
  // "no matches" line never flashes before the first answer.
  return {
    groups,
    isLoading: trimmed !== '' && !data && !query.isError,
    isError: query.isError,
    // The rows answer the current text, not the previous query kept on screen while it loads.
    settled: effectiveQuery !== '' && effectiveQuery === trimmed && query.isSuccess && !query.isPlaceholderData,
  };
}
